/**
 * useEntityRecordScope
 *
 * Convenience reader that resolves the nearest `PageScope` in the tree and
 * binds the keyed store for its IDs. Use this from leaf consumers
 * (`Entity`, `ArTable`, `useEntityRecordUI`, attachment writers) so they can
 * read selected entity / record / writers with a single call.
 *
 * Returns `insideEntityRecordContext: false` and empty slots outside an
 * entity scope, so consumers can branch on it without null checks. Under the
 * layout editor's `preview` scope the mocked store is returned verbatim, so
 * nothing touches Firestore.
 */
import { useContext } from 'react';
import { useSnapshot } from 'valtio';
import { PageScopeContext } from '../pageScope';
import {
    useEntityRecordStore,
    type UseEntityRecordStoreReturn,
} from './useEntityRecordStore';

export const useEntityRecordScope = (): UseEntityRecordStoreReturn => {
    const scope = useSnapshot(useContext(PageScopeContext));
    const store = useEntityRecordStore(
        scope.source === 'entity' ? scope.entityId : '',
        scope.source === 'entity' ? scope.recordId : undefined,
    );
    return scope.source === 'preview' ? scope.preview : store;
};
